import { getRoutePolyline, getTerminalCoords } from '@/data/coordinates';
import type { RoutePoint } from '@/data/types';

export type LngLat = [number, number];

const COORDINATE_MARKER = '__STARLINE_COORDS__';

/**
 * Route counters pre-date map editing and only have a free-form `location`
 * column. Keep the address intact and append a private, parseable coordinate
 * marker so map placements survive a save without a schema fork.
 */
export function encodeRoutePointLocation(address: string, coordinates?: LngLat): string {
  const cleanAddress = address.replace(new RegExp(`\\s*${COORDINATE_MARKER}:.*$`), '').trim();
  if (!coordinates) return cleanAddress;
  return `${cleanAddress}${cleanAddress ? ' ' : ''}${COORDINATE_MARKER}:${coordinates[0]},${coordinates[1]}`;
}

export function decodeRoutePointLocation(location: string | null | undefined): {
  address: string;
  coordinates?: LngLat;
} {
  const raw = typeof location === 'string' ? location : '';
  const markerIndex = raw.lastIndexOf(`${COORDINATE_MARKER}:`);
  if (markerIndex < 0) return { address: raw.trim() };

  const address = raw.slice(0, markerIndex).trim();
  const value = raw.slice(markerIndex + COORDINATE_MARKER.length + 1).trim();
  const [lng, lat] = value.split(',').map(Number);
  if (!Number.isFinite(lng) || !Number.isFinite(lat) || Math.abs(lng) > 180 || Math.abs(lat) > 90) {
    return { address };
  }
  return { address, coordinates: [lng, lat] };
}

function interpolatePolyline(polyline: LngLat[], index: number, count: number): LngLat | undefined {
  if (polyline.length === 0) return undefined;
  if (polyline.length === 1 || count <= 1) return polyline[0];
  const position = (Math.max(0, Math.min(index, count - 1)) / (count - 1)) * (polyline.length - 1);
  const lower = Math.floor(position);
  const upper = Math.min(polyline.length - 1, lower + 1);
  const fraction = position - lower;
  return [
    polyline[lower][0] + (polyline[upper][0] - polyline[lower][0]) * fraction,
    polyline[lower][1] + (polyline[upper][1] - polyline[lower][1]) * fraction,
  ];
}

/**
 * Prefer an explicitly placed coordinate, then a registered terminal, and
 * finally a stable point along the known road corridor. The last fallback is
 * visual-only until the operator places the stop.
 */
export function resolveRoutePointCoordinate(
  point: RoutePoint,
  index: number,
  points: RoutePoint[],
  routeId: string,
): LngLat | undefined {
  if (point.coordinates) return point.coordinates;
  if (point.counterId) {
    const terminal = getTerminalCoords(point.counterId);
    if (terminal) return [terminal.lng, terminal.lat];
  }
  return interpolatePolyline(getRoutePolyline(routeId), index, points.length);
}

export function resolveRoutePointCoordinates(routeId: string, points: RoutePoint[]): Array<LngLat | undefined> {
  return points
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((point, index, sorted) => resolveRoutePointCoordinate(point, index, sorted, routeId));
}
